import { z } from 'zod'

import { dateGteIso, dateLtIso } from './date-utils'
import { getHostRuntime } from '@/Atomic/Widgets/AiChatBot/core/engine/host-runtime'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import type { SessionContext } from '@/Atomic/Widgets/AiChatBot/core/engine/types'
import { type ScrivitoAccessor } from '@/Atomic/Widgets/AiChatBot/core/tools/shared/entity-serializer'
import { DataClassName } from '@/Data/const'

export const fetchOrdersPortfolioTool = defineTool({
  name: 'fetch_orders_portfolio',
  description:
    'Fetch orders portfolio for one or more account directors within a date range. Returns per-order data: name, client, type, revenue, totalBudget, usedBudget, remainingBudget, totalDays, usedDays, remainingDays, recordedDays. Also returns portfolio totals and flags for orders over 90% budget or with zero days.',
  inputSchema: () =>
    z.object({
      accountDirectorIds: z
        .array(z.string())
        .min(1)
        .describe('One or more account director user IDs to include'),
      startDate: z.string().describe('Range start in YYYY-MM-DD format'),
      endDate: z.string().describe('Range end in YYYY-MM-DD format'),
    }),
  concurrencySafe: true,

  async run(
    args: {
      accountDirectorIds: string[]
      startDate: string
      endDate: string
    },
    _ctx: SessionContext,
  ) {
    const { accountDirectorIds, startDate, endDate } = args
    const { data } = getHostRuntime()

    if (
      !data.resolveDataClass(DataClassName.TIMERECORD_ORDER) ||
      !data.resolveDataClass(DataClassName.TIMERECORD_BUDGET) ||
      !data.resolveDataClass(DataClassName.TIMERECORD_CLIENT)
    ) {
      throw new Error('Required TimeRecord DataClasses not found')
    }

    const dateFilters = {
      'recordedAt.gte': dateGteIso(startDate),
      'recordedAt.lt': dateLtIso(endDate),
    }

    // 1. For each account director, get orders
    const allOrders = (
      await Promise.all(
        accountDirectorIds.map(async (accountDirectorId) => {
          const orders = (await data.query(DataClassName.TIMERECORD_ORDER, {
            filters: { accountDirector: accountDirectorId },
          })) as ScrivitoAccessor[]
          return orders.map((order) => ({ order, accountDirectorId }))
        }),
      )
    ).flat()

    // 2. For each order, fetch budget + client
    const orderData = await Promise.all(
      allOrders.map(async ({ order, accountDirectorId }) => {
        const orderId = order.id()
        const clientId = order.get('client') as string
        const orderType = (order.get('type') as string) ?? 'Unknown'
        const orderName = (order.get('name') as string) ?? 'Unnamed Order'

        const [budgets, clientItem] = await Promise.all([
          data.query(DataClassName.TIMERECORD_BUDGET, {
            filters: { order: orderId, ...dateFilters },
            limit: 1,
          }) as Promise<ScrivitoAccessor[]>,
          clientId
            ? (data.get(
                DataClassName.TIMERECORD_CLIENT,
                clientId,
              ) as Promise<ScrivitoAccessor | null>)
            : null,
        ])

        const budget = budgets[0]
        const revenue = (budget?.get('revenue') as number) ?? 0
        const totalBudget = (budget?.get('totalBudget') as number) ?? 0
        const usedBudget = (budget?.get('usedBudget') as number) ?? 0
        const remainingBudget = (budget?.get('remainingBudget') as number) ?? 0
        const totalDays = (budget?.get('totalDays') as number) ?? 0
        const usedDays = (budget?.get('usedDays') as number) ?? 0
        const remainingDays = (budget?.get('remainingDays') as number) ?? 0
        const recordedDays = (budget?.get('recordedDays') as number) ?? 0

        const clientName =
          (clientItem?.get('name') as string) ?? 'Unknown Client'

        const budgetUtilizationPct =
          totalBudget > 0 ? (usedBudget / totalBudget) * 100 : 0

        return {
          orderId,
          name: orderName,
          clientId: clientId ?? null,
          clientName,
          orderType,
          accountDirectorId,
          revenue,
          totalBudget,
          usedBudget,
          remainingBudget,
          totalDays,
          usedDays,
          remainingDays,
          recordedDays,
          budgetUtilizationPct,
        }
      }),
    )

    // 3. Totals
    const totals = {
      totalRevenue: orderData.reduce((sum, o) => sum + o.revenue, 0),
      totalBudget: orderData.reduce((sum, o) => sum + o.totalBudget, 0),
      totalUsedBudget: orderData.reduce((sum, o) => sum + o.usedBudget, 0),
      avgBudgetUtilizationPct:
        orderData.length > 0
          ? orderData.reduce((sum, o) => sum + o.budgetUtilizationPct, 0) /
            orderData.length
          : 0,
    }

    // 4. Flags
    const flags: Array<{ orderId: string; reason: string }> = []
    for (const order of orderData) {
      if (order.budgetUtilizationPct > 90) {
        flags.push({ orderId: order.orderId, reason: 'budget-over-90' })
      }
      if (order.totalDays > 0 && order.recordedDays === 0) {
        flags.push({ orderId: order.orderId, reason: 'inactive-zero-days' })
      }
    }

    return {
      period: { start: startDate, end: endDate },
      orders: orderData,
      totals,
      flags,
      _instruction:
        'Present an orders portfolio summary. Highlight: orders with budgetUtilizationPct > 90, orders with remainingBudget < 20% of totalBudget, orders flagged as inactive-zero-days. Use a table or bullet list for clarity.',
    }
  },
})
